import { Card, CardContent, Typography, Box, Tooltip, Checkbox } from "@mui/material";
import { IImageInfo, IProjectData } from "@types";
import { imageFlat } from "../../utils/utils";
import MouseClickList from "../MouseClickList";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { deleteDataset, deleteProject, generateUniqueProjectName, truncateString } from "common-lib";
import { createProject, getProject, getProjectGeneralInfo } from "../../services/projectService";
import { selectedProjectState } from "../../recoil";
import { useRecoilState, useSetRecoilState } from "recoil";
import { t } from "i18next";
import { toast } from "react-toastify";
import { getTutorialContinue } from "../../utils/tutorialUtil";
import { useTutorial } from "../../hooks/useTutorial";
import { tutorialFlowMap } from "../joyride/tutorialSteps";
import { FileIcon, Folder, Info, MoreVertical } from "lucide-react";
import { FileTypeBadge } from "./FileTypeBadge";

interface IProjectCardProps {
  data: IProjectData;
  setProjectsData: (projectsData: IProjectData[]) => void;
  projectData: IProjectData[];
  setIsCopied: (isCopied: boolean) => void;
  checkedProjects: number[];
  setCheckedProjects: Dispatch<SetStateAction<number[]>>;
  onDragStart: (e: React.DragEvent, item: string) => void;
  onDragEnd: (e: React.DragEvent) => void;
  className: string;
  setDeletedId: (deletedId: number | null) => void;
}

export default function ProjectCard({
  data,
  setProjectsData,
  projectData,
  setIsCopied,
  checkedProjects,
  setCheckedProjects,
  onDragStart,
  onDragEnd,
  className,
  setDeletedId,
}: IProjectCardProps) {
  const navigate = useNavigate();
  // const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const { project_name, step, material_name, images, imageCount, NGCount } = data;
  const [selectedProject, setSelectedProject] = useRecoilState(selectedProjectState);
  const [checked, setChecked] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    setChecked(checkedProjects.includes(data.id));
  }, [checkedProjects, data.id]);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setChecked((prev) => !prev);

    setCheckedProjects((prev: number[]) => {
      if (prev.includes(data.id)) {
        return prev.filter((id) => id !== data.id);
      } else {
        return [...prev, data.id];
      }
    });
  };

  // const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
  //   event.preventDefault();
  //   setAnchorEl(event.currentTarget);
  // };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const open = Boolean(contextMenu);
  const handleClose = () => {
    setContextMenu(null);
  };

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (data.step !== "분석") {
      navigate(`/main/project/${data.id}`);
    } else {
      const response = await getProjectGeneralInfo(data.id);
      if (response.project.class.length < 1) {
        navigate(`/main/project/${data.id}/analysis/pre_class`);
      } else {
        navigate(`/main/project/${data.id}/analysis`);
      }
    }
    setSelectedProject(data.id);
  };

  return (
    // 기존 inline style → Mac 토큰 기반으로 전환
    // FileIcon, FileTypeBadge, MouseClickList, truncateString, Checkbox 등 기존 import 그대로 사용

    <>
      <div
        id="project-card"
        className="group relative"
        style={{
          background: "var(--dk-surface, white)",
          borderRadius: "14px",
          border: `0.5px solid ${hovered ? "rgba(37,99,235,0.25)" : "rgba(0,0,0,0.09)"}`,
          boxShadow: hovered ? "0 6px 20px rgba(0,0,0,0.10)" : "0 1px 3px rgba(0,0,0,0.06)",
          transform: hovered ? "translateY(-2px)" : "none",
          transition: "all 0.16s",
          cursor: "pointer",
          overflow: "hidden",
          fontFamily: `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif`,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          if (!open) setHovered(false);
        }}
        onClick={handleOpen}
        onContextMenu={handleContextMenu}
        onDragOver={(e) => e.preventDefault()}
        onDragStart={(e) => onDragStart(e, data.project_name)}
        onDragEnd={onDragEnd}
      >
        <div style={{ padding: "14px" }}>
          {/* Checkbox — hover 또는 checked 시 노출 */}
          {(hovered || checked) && (
            <Box
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                zIndex: 2,
                width: 18,
                height: 18,
                borderRadius: "5px",
                border: checked ? "none" : "1.5px solid #d1d5db",
                background: checked ? "#2563eb" : "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.12s",
                cursor: "pointer",
              }}
              onClick={handleCheckboxClick}
            >
              {checked && (
                <Box
                  sx={{
                    width: 9,
                    height: 5,
                    borderLeft: "1.5px solid white",
                    borderBottom: "1.5px solid white",
                    transform: "rotate(-45deg) translateY(-1px)",
                  }}
                />
              )}
            </Box>
          )}

          {/* 아이콘 + 뱃지 */}
          <div
            style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "10px" }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: "9px",
                background: "var(--dk-info-bg, #eff6ff)",
                border: "0.5px solid var(--dk-info-border, #bfdbfe)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FileIcon size={16} style={{ color: "#2563eb" }} />
            </div>
            <FileTypeBadge type={data.step} size="sm" />
          </div>

          {/* 프로젝트명 */}
          <div
            style={{
              fontSize: "13px",
              fontWeight: 700,
              color: "var(--dk-text, #1d1d1f)",
              letterSpacing: "-0.02em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginBottom: "3px",
            }}
            title={data.project_name}
          >
            {truncateString(data.project_name, 20)}
          </div>

          {/* 자재명 */}
          <p
            style={{
              fontSize: "11px",
              color: "var(--dk-text-3, #aeaeb2)",
              margin: "0 0 10px",
              letterSpacing: "-0.01em",
            }}
          >
            {t("Material")}: {data.material_name || "-"}
          </p>

          {/* 라벨링 progress — 검사 타입 제외 */}
          {data.step !== "검사" && (
            <div
              style={{
                padding: "7px 9px",
                background: "var(--dk-surface-2, #fafafa)",
                borderRadius: "9px",
                border: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "5px" }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    color: "var(--dk-text-3, #aeaeb2)",
                    fontWeight: 500,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                >
                  {t("Labeling")}
                </span>
                <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--dk-text, #1d1d1f)" }}>
                  {data.labelCount}
                  {t("SheetsUnit")} / {data.imageCount}
                  {t("SheetsUnit")}
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "3px",
                  borderRadius: "99px",
                  background: "#e8e8ed",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    borderRadius: "99px",
                    background: "#2563eb",
                    width: `${data.imageCount ? Math.round((data.labelCount / data.imageCount) * 100) : 0}%`,
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <ProjectContextMenu
        data={data}
        contextMenu={contextMenu}
        onClose={handleClose}
        projectData={projectData}
        setProjectsData={setProjectsData}
        setIsCopied={setIsCopied}
      />
    </>
  );
}

interface IProjectContextMenuProps {
  data: IProjectData;
  contextMenu: { x: number; y: number } | null;
  onClose: () => void;
  projectData: IProjectData[];
  setProjectsData: (projectsData: IProjectData[]) => void;
  setIsCopied: (isCopied: boolean) => void;
}

// 우클릭 메뉴(복사/수정/삭제/AI 연동 제거) — 카드/리스트 뷰 공용
export function ProjectContextMenu({
  data,
  contextMenu,
  onClose,
  projectData,
  setProjectsData,
  setIsCopied,
}: IProjectContextMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const setSelectedProject = useSetRecoilState(selectedProjectState);

  const handleCopy = async () => {
    const isContinue = getTutorialContinue();

    try {
      const response = await getProject(data.id);
      if (!response || !response.project) {
        // throw new Error("Failed to retrieve project data.");
        toast.error(t("FailedToRetrieveProjectData"));
      }
      const fileNames = imageFlat(response.project.images).map((file: IImageInfo) => file.path);
      const newProjectName = await generateUniqueProjectName(response.project.project_name);

      const toastThreshold = 1000; // 밀리초 단위 (1초)
      let lastToastTime = Date.now();

      toast.info(t("CopyProjectStartMsg"));
      lastToastTime = Date.now();

      const createResponse = await createProject({
        project_name: newProjectName,
        step: response.project.step,
        images: fileNames,
        material_name: response.project.material_name,
        train_id: response.project.train_id || null,
      });
      console.log("createResponse", createResponse);

      if (isContinue) {
        // startTutorial("/home", 0);
        const currentPath = location.pathname;
        const nextPath = tutorialFlowMap[currentPath];
        if (nextPath) {
          const finalPath = nextPath.replace(":id", createResponse.project.id);
          navigate(finalPath);
          return;
        }
      }

      if (!createResponse) {
        throw new Error("Failed to create the copied project.");
      }

      if (createResponse.project.step !== "분석") {
        navigate(`/project/${createResponse.project.id}`);
      } else {
        const response = await getProject(createResponse.project.id);
        if (response.project.class.length < 1) {
          navigate(`/project/${createResponse.project.id}/analysis/pre_class`);
        } else {
          navigate(`/project/${createResponse.project.id}/analysis`);
        }
      }
      const now = Date.now();
      if (now - lastToastTime > toastThreshold) {
        toast.success(t("MoveToCopiedProjectMsg"));
      }
      setIsCopied(true);
      // setProjectsData([...projectData, createResponse]);
    } catch (error) {
      console.error(error);
    } finally {
      onClose();
    }
  };

  const handleEdit = async () => {
    onClose();
    navigate(`/main/project/${data.id}/setting`);
  };

  const handleDelete = async () => {
    try {
      if (window.confirm(t("DeleteConfirmMsg"))) {
        console.log(data);
        const response = await deleteProject(data.id);

        // if (data.step === "분석") {
        //   await deleteDataset(data.dataSet_id as number);
        // }
        toast.success(response.message);
        setProjectsData(projectData.filter((project) => project.id !== data.id));
      }
    } catch (error) {
      console.error(error);
    } finally {
      onClose();
    }
  };

  return (
    <MouseClickList
      contextMenu={contextMenu}
      open={Boolean(contextMenu)}
      category="project"
      onClose={onClose}
      onCopy={handleCopy}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dataId={data.id}
    />
  );
}
